import { preferenceToggle } from "../notifications/controller.ts";
import { toastItem } from "../../ui/primitives.ts";
import { weeklyReviewReminderDue, weekKey, type WeeklyReviewReminder } from "./weekly-review.ts";

export interface WeeklyReviewReminderCapabilities {
  state(): WeeklyReviewReminder;
  setState(reminder: WeeklyReviewReminder): void;
  persist(reminder: WeeklyReviewReminder): Promise<void>;
  hasUser(): boolean;
  toast(message: string): void;
  startReview(): void;
  notify?(title: string, options: NotificationOptions): void;
}

export function createWeeklyReviewReminderRuntime(capabilities: WeeklyReviewReminderCapabilities) {
  return {
    check: (now?: Date): Promise<void> => checkWeeklyReviewReminder(capabilities, now),
    openSettings: (): void => openWeeklyReviewReminderSettings(capabilities),
  };
}

export function reminderFromForm(data: FormData, previous: WeeklyReviewReminder): WeeklyReviewReminder {
  return {
    enabled: data.has("enabled"),
    weekday: Number(data.get("weekday")),
    hour: Number(String(data.get("hour") || "17:00").slice(0, 2)),
    lastPromptWeek: previous.lastPromptWeek,
  };
}

export function openWeeklyReviewReminderSettings(capabilities: WeeklyReviewReminderCapabilities): void {
  const reminder = capabilities.state();
  const dialog = document.createElement("dialog");
  dialog.className = "name-dialog";
  dialog.setAttribute("aria-label", "Weekly review reminder");
  const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  dialog.innerHTML = `<form><p class="eyebrow">Device-private</p><h2>Weekly review reminder</h2><p>Show a reminder when WeirDo is open on your chosen day. Browser notification permission is optional.</p>${preferenceToggle("enabled", "Remind me each week", reminder.enabled)}<div class="field-grid"><label class="field-label">Day<select name="weekday">${days.map((day, index) => `<option value="${index}" ${reminder.weekday === index ? "selected" : ""}>${day}</option>`).join("")}</select></label><label class="field-label">After<input name="hour" type="time" value="${String(reminder.hour).padStart(2, "0")}:00"></label></div><div class="dialog-actions"><button type="button" class="button button--soft" data-cancel>Cancel</button><button class="button button--primary">Save reminder</button></div></form>`;
  dialog.querySelector("[data-cancel]")!.addEventListener("click", () => dialog.close());
  dialog.querySelector("form")!.addEventListener("submit", async event => {
    event.preventDefault();
    const next = reminderFromForm(new FormData(event.currentTarget as HTMLFormElement), capabilities.state());
    capabilities.setState(next);
    await capabilities.persist(next);
    dialog.close();
    capabilities.toast("Weekly review reminder saved");
  });
  document.body.append(dialog);
  dialog.addEventListener("close", () => dialog.remove());
  dialog.showModal();
}

export async function checkWeeklyReviewReminder(capabilities: WeeklyReviewReminderCapabilities, now = new Date()): Promise<void> {
  const reminder = capabilities.state();
  if (!capabilities.hasUser() || !weeklyReviewReminderDue(reminder, now)) return;
  const next = { ...reminder, lastPromptWeek: weekKey(now) };
  capabilities.setState(next);
  await capabilities.persist(next);
  capabilities.notify?.("Weekly review is ready", { body: "Take a few minutes to review unfinished and recently completed work.", tag: `weekly-review-${next.lastPromptWeek}` });
  const region = document.querySelector<HTMLElement>("#toast-region");
  if (!region) return;
  const item = toastItem("Weekly review is ready");
  item.classList.add("toast--action");
  const button = document.createElement("button");
  button.type = "button";
  button.textContent = "Start";
  button.addEventListener("click", () => { item.remove(); capabilities.startReview(); });
  item.append(button);
  region.append(item);
}
